import React, { useState, useCallback, useRef, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import {
  DialogContent,
  DialogTitle,
  Typography,
  Select,
  MenuItem,
} from '@material-ui/core'
import { Button, Checkbox } from 'sputnik-ui'
import { Formik, Form } from 'formik'
import styled from 'styled-components'
import FormFieldWithError from 'components/atoms/FormFieldWithError'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { createEmergency, getEmergencySounds } from 'store/operations'
import StyledActionsDialog from 'components/atoms/StyledActionsDialog'
import StyledModalDialog from 'components/atoms/StyledModalDialog'
import Transition from 'components/atoms/Transition'
import ChooseEmergencyAddresses from '../ChooseEmergencyAddresses'

const styles = {
  field: { marginTop: '0.5rem' },
  button: { margin: '0px', padding: '0px', marginRight: 5 },
  divGroup: { display: 'flex', gap: '1rem', marginTop: '0.5rem' },
}
const StyledFormTypography = styled(Typography)`
  && {
    margin-top: 1rem;
    font-weight: 400;
  }
`

const checkAudioDuration = (file) => {
  return new Promise((resolve) => {
    const audio = new Audio()
    audio.src = URL.createObjectURL(file)

    audio.addEventListener('loadedmetadata', () => {
      const { duration } = audio
      URL.revokeObjectURL(audio.src) // Clean up
      resolve(duration)
    })

    audio.addEventListener('error', () => {
      resolve(Infinity)
    })
  })
}

const CreateEmergencyCallModal = ({
  open,
  handleClose,
  currentUser,
  ...props
}) => {
  const { t } = useTranslation(['common', 'emergency'])
  const dispatch = useDispatch()
  const file = useRef(null)
  const [loading, setLoading] = useState(false)

  const schema = Yup.object().shape({
    duration: Yup.number()
      .positive(t('common:field.error.number.positive'))
      .integer(t('common:field.error.number.int'))
      .required(t('common:field.error.required')),
    title: Yup.string().required(t('common:field.error.required')),
    audioFile: Yup.mixed()
      .required(t('common:field.error.required'))
      .test(
        'audio-duration',
        t('emergency:create.audio_too_long'),
        async (file) => {
          if (typeof file === 'string' || typeof file === 'number') {
            return true
          }

          if (!file) return false

          try {
            const audioDuration = await checkAudioDuration(file)
            return audioDuration <= 30
          } catch (error) {
            return false
          }
        }
      ),
    addresses: Yup.object().test(
      'has-addresses',
      t('common:field.error.required'),
      (val) => val && Object.keys(val).length > 0
    ),
  })

  const [openChooseAddress, setOpenChooseAddress] = useState(false)
  const [emergencySounds, setEmergencySounds] = useState([])
  const [audioObj, setAudioObj] = useState({
    file: null,
    fileName: '',
    error: '',
    selectedSoundId: null,
  })

  useEffect(() => {
    dispatch(getEmergencySounds()).then((result) => {
      setEmergencySounds(result)
    })
  }, [dispatch])

  const handleCloseLocal = useCallback(() => {
    if (file.current) {
      file.current.value = ''
    }
    setAudioObj({
      file: null,
      fileName: '',
      error: null,
      selectedSoundId: null,
    })
    handleClose()
  }, [handleClose])

  const handleSubmit = useCallback(
    (values) => {
      setLoading(true)
      dispatch(
        createEmergency({
          title: values.title,
          address_uuids: JSON.stringify(values.addresses),
          audio_file: values.audioFile,
          duration: values.duration * 60,
          kind: 'call',
          audio_id: audioObj.selectedSoundId,
          door_open: values.openDoor,
        })
      ).then(handleCloseLocal)
    },
    [dispatch, handleCloseLocal, audioObj.selectedSoundId]
  )

  const handleFileChange = async (event, setFieldValue, setFieldError) => {
    const selectedFile = event.target.files[0]
    if (selectedFile) {
      try {
        const audioDuration = await checkAudioDuration(selectedFile)

        if (audioDuration > 30) {
          setAudioObj({
            file: null,
            fileName: '',
            error: t('emergency:create.audio_too_long'),
            selectedSoundId: null,
          })
          setFieldError('audioFile', t('emergency:create.audio_too_long'))
          setFieldValue('audioFile', null)
        } else {
          setAudioObj({
            file: selectedFile,
            fileName: selectedFile.name,
            error: '',
            selectedSoundId: null,
          })
          setFieldValue('audioFile', selectedFile)
          setFieldError('audioFile', '')
        }
      } catch (error) {
        setAudioObj({
          file: null,
          fileName: '',
          error: t('emergency:create.audio_invalid'),
          selectedSoundId: null,
        })
        setFieldError('audioFile', t('emergency:create.audio_invalid'))
        setFieldValue('audioFile', null)
      }
    }
  }

  const handleSoundSelect = (soundId, setFieldValue) => {
    setAudioObj({
      file: null,
      fileName: '',
      error: '',
      selectedSoundId: soundId,
    })
    setFieldValue('audioFile', soundId)
  }

  const handleUploadClick = () => {
    file.current.click()
  }

  const clearAudioSelection = (setFieldValue) => {
    setAudioObj({
      file: null,
      fileName: '',
      error: '',
      selectedSoundId: null,
    })
    setFieldValue('audioFile', null)
  }

  return (
    <StyledModalDialog
      open={open}
      fullWidth
      maxWidth="sm"
      TransitionComponent={Transition}
      onClose={handleCloseLocal}
      PaperProps={{
        style: { borderRadius: 20, maxHeight: '100vh' },
      }}
      {...props}
    >
      <DialogTitle>{t('emergency:create.modal_title_call')}</DialogTitle>

      <Formik
        validationSchema={schema}
        onSubmit={handleSubmit}
        initialValues={{
          title: '',
          audioFile: null,
          duration: '',
          addresses: {},
          openDoor: false,
        }}
      >
        {({ touched, errors, setFieldValue, values, setFieldError }) => {
          const handleAddressChange = (streetUuid, houseUuid) => {
            const newSelected = { ...values.addresses }
            if (newSelected[streetUuid] && !houseUuid) {
              delete newSelected[streetUuid]
              setFieldValue('addresses', newSelected)
              return
            }
            newSelected[streetUuid] = newSelected[streetUuid] || []
            if (houseUuid) {
              const housesSelected = [...newSelected[streetUuid]]
              const currentIndex = housesSelected.indexOf(houseUuid)
              if (currentIndex === -1) {
                housesSelected.push(houseUuid)
              } else {
                housesSelected.splice(currentIndex, 1)
              }
              newSelected[streetUuid] = housesSelected
            }
            setFieldValue('addresses', newSelected)
          }

          const selectAllAddresses = (uuids) => {
            if (
              Object.keys(uuids).length === Object.keys(values.addresses).length
            ) {
              setFieldValue('addresses', {})
            } else {
              setFieldValue('addresses', uuids)
            }
          }

          return (
            <Form>
              <DialogContent>
                <StyledFormTypography variant="h6">
                  {t('emergency:create.title_field')}
                </StyledFormTypography>
                <FormFieldWithError
                  fullWidth
                  name="title"
                  style={styles.field}
                  error={errors.title}
                  touched={touched.title}
                />
                <StyledFormTypography variant="h6">
                  {t('emergency:communication_channels.desc')}
                </StyledFormTypography>
                <div>
                  <div style={styles.divGroup}>
                    <Checkbox
                      color="blue"
                      name="openDoor"
                      defaultChecked
                      disabled
                      style={styles.button}
                    />

                    <Typography color="textSecondary">
                      {t('emergency:communication_channels.intercom_call')}
                    </Typography>
                  </div>
                  <div style={styles.divGroup}>
                    <Checkbox
                      color="blue"
                      defaultChecked
                      disabled
                      style={styles.button}
                    />

                    <Typography color="textSecondary">
                      {t('emergency:communication_channels.intercom_main')}
                    </Typography>
                  </div>
                </div>

                <StyledFormTypography variant="h6">
                  {t('emergency:create.keep_door_open_title')}
                </StyledFormTypography>
                <div style={styles.divGroup}>
                  <Checkbox
                    color="blue"
                    name="openDoor"
                    onClick={(e) => setFieldValue('openDoor', e.target.checked)}
                    style={styles.button}
                  />

                  <Typography color="textSecondary">
                    {t('emergency:create.keep_door_open_desc')}
                  </Typography>
                </div>

                <div>
                  <div>
                    <StyledFormTypography variant="h6">
                      {t('emergency:create.audio_file')}
                    </StyledFormTypography>
                    <Typography color="textSecondary">
                      {t('emergency:create.audio_file_spec')}
                    </Typography>
                    {(audioObj.error || errors.audioFile) && (
                      <Typography color="error" variant="caption">
                        {audioObj.error || errors.audioFile}
                      </Typography>
                    )}
                  </div>

                  <div style={styles.divGroup}>
                    <Select
                      placeholder={t('emergency:create.choose_audio')}
                      isMulti={false}
                      style={{ paddingLeft: 50 }}
                      onChange={(e) => {
                        handleSoundSelect(e.target.value, setFieldValue)
                      }}
                      disabled={!!audioObj.fileName}
                      value={audioObj.selectedSoundId || ''}
                    >
                      {emergencySounds.length > 0 &&
                        emergencySounds.map((sound) => (
                          <MenuItem key={sound.id} value={sound.blob_id}>
                            {sound.audioname}
                          </MenuItem>
                        ))}
                    </Select>
                    {audioObj.fileName && (
                      <Typography color="textSecondary">
                        {t('emergency:create.uploaded_file')}{' '}
                        {audioObj.fileName}
                      </Typography>
                    )}

                    <div
                      style={{
                        display: 'flex',
                        gap: '0.5rem',
                        marginTop: '1rem',
                      }}
                    >
                      <Button
                        primary
                        variant="v2"
                        onClick={handleUploadClick}
                        size="small"
                      >
                        {audioObj.selectedSoundId || audioObj.fileName
                          ? t('common:button.change')
                          : t('common:file.upload')}
                      </Button>
                    </div>
                  </div>
                  <input
                    id="audioFile"
                    name="audioFile"
                    accept=".wav"
                    type="file"
                    onChange={(e) =>
                      handleFileChange(e, setFieldValue, setFieldError)
                    }
                    ref={file}
                    style={{ display: 'none' }}
                  />
                </div>

                <div style={styles.divGroup}>
                  <div>
                    <StyledFormTypography variant="h6">
                      {t('devices:units.intercom_plural')}
                    </StyledFormTypography>
                    <Typography color="textSecondary">
                      {`${t('emergency:create.intercom_count')}: ${
                        Object.keys(values.addresses).length || 0
                      }`}
                      {errors.addresses && (
                        <Typography color="error" variant="caption">
                          {errors.addresses}
                        </Typography>
                      )}
                    </Typography>
                  </div>
                  <Button
                    primary
                    variant="v2"
                    size="small"
                    style={{ marginTop: '1rem' }}
                    onClick={() => {
                      setOpenChooseAddress(true)
                    }}
                  >
                    {t('common:button.change')}
                  </Button>
                </div>

                <StyledFormTypography variant="h6">
                  {t('devices:video_archive.duration_date')}
                </StyledFormTypography>
                <FormFieldWithError
                  style={styles.field}
                  fullWidth
                  label={t('emergency:create.duration_desc')}
                  name="duration"
                  type="number"
                  min="1"
                  step="1"
                  error={errors.duration}
                  touched={touched.duration}
                />
              </DialogContent>

              <StyledActionsDialog>
                <Button
                  type="submit"
                  primary
                  variant="v2"
                  fullWidth
                  disabled={loading}
                  style={{ marginBottom: '1rem', width: '20rem' }}
                >
                  {t('common:button.create')}
                </Button>
                <Button onClick={handleCloseLocal} tertiary>
                  {t('devices:video_archive.back')}
                </Button>
              </StyledActionsDialog>

              <ChooseEmergencyAddresses
                open={openChooseAddress}
                handleClose={() => setOpenChooseAddress(false)}
                handleAddressChange={handleAddressChange}
                choosenAddresses={values.addresses}
                selectAll={selectAllAddresses}
                onSuccess={() => {
                  setOpenChooseAddress(false)
                }}
              />
            </Form>
          )
        }}
      </Formik>
    </StyledModalDialog>
  )
}

export default CreateEmergencyCallModal
